import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Bell, X, CheckCheck, Clock, Car } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) return;
      const res = await axios.get(`${API}/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setCount(res.data.count || 0);
    } catch (e) {
      console.error("Notification fetch error:", e);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = async (bookingId) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/admin/notifications/read/${bookingId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== bookingId));
      setCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error("Mark read error:", e);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/admin/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
      setCount(0);
    } catch (e) {
      console.error("Mark all read error:", e);
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return "tani";
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} orë`;
    return `${Math.floor(diff / 86400)} ditë`;
  };

  const statusColor = {
    pending: "text-yellow-500",
    confirmed: "text-blue-500",
    cancelled: "text-red-500",
  };

  const statusLabel = {
    pending: "Në pritje",
    confirmed: "Konfirmuar",
    cancelled: "Anulluar",
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-gray-400 hover:text-white hover:bg-zinc-800 transition-colors"
        data-testid="notifications-bell"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 animate-pulse" data-testid="notifications-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-zinc-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notifications-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-zinc-950/50">
            <h3 className="text-white font-semibold text-sm">Njoftimet</h3>
            <div className="flex items-center gap-2">
              {count > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-yellow-500 hover:text-yellow-400 flex items-center gap-1 transition-colors"
                  data-testid="mark-all-read"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Lexo të gjitha
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Notifications list */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Nuk ka njoftime të reja</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className="px-4 py-3 border-b border-gray-800/50 hover:bg-zinc-800/50 transition-colors group"
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-yellow-600/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Car className="h-4 w-4 text-yellow-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-white text-sm font-medium truncate">{n.customer_name}</p>
                        <span className="text-gray-500 text-[11px] flex-shrink-0 ml-2">{timeAgo(n.created_at)}</span>
                      </div>
                      <p className="text-gray-400 text-xs mt-0.5">{n.car_name}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-gray-500 text-[11px]">{n.pickup_date?.slice(0, 10)} → {n.return_date?.slice(0, 10)}</span>
                        <span className={`text-[11px] ${statusColor[n.status] || "text-gray-400"}`}>{statusLabel[n.status] || n.status}</span>
                        <span className="text-yellow-500 text-[11px] font-medium">€{n.total_price?.toFixed?.(2) || n.total_price}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); markAsRead(n.id); }}
                      className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-green-400 transition-all flex-shrink-0"
                      title="Shëno si të lexuar"
                      data-testid={`mark-read-${n.id}`}
                    >
                      <CheckCheck className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {count > 0 && (
            <div className="px-4 py-2 border-t border-gray-800 bg-zinc-950/50">
              <p className="text-gray-500 text-[11px] text-center">{count} njoftim{count !== 1 ? "e" : ""} të pa-lexuara</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
